import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { ErrorBoundary } from "./ErrorBoundary";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import {
  LayoutDashboard, Car, CalendarRange, MapPin, Wrench,
  Users, CreditCard, Bot, LogOut, Bell, Search, FileSignature,
  Settings as SettingsIcon, Receipt, Wallet, UserCog,
  AlertTriangle, ShieldAlert, ShieldX, BarChart3, Building2, Crown, FileText, Calculator, Brain, Smartphone,
  ShieldCheck, Tag, Plug,
  CalendarRange as Gantt, TrendingUp, Eye, BellRing,
  Truck, Globe, Activity, HardDrive, AlertCircle, MessageCircle,
  Menu, X, KeyRound
} from "lucide-react";
import { Brand } from "./Brand";
import LangSwitcher from "./LangSwitcher";
import CommandPalette from "./CommandPalette";
import ChangePasswordDialog from "./ChangePasswordDialog";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const buildItems = (t) => [
  { to: "/admin", icon: LayoutDashboard, label: t("common.dashboard"), end: true, key: "dashboard" },
  { to: "/admin/fleet", icon: Car, label: t("common.fleet"), key: "flotta" },
  { to: "/admin/planning", icon: Gantt, label: t("common.planning") + " ⚡", key: "planning" },
  { to: "/admin/tariffe", icon: Tag, label: t("common.tariffs"), key: "tariffe" },
  { to: "/admin/assicurazioni", icon: ShieldCheck, label: t("common.insurance"), key: "assicurazioni" },
  { to: "/admin/contracts", icon: FileSignature, label: t("common.contracts"), key: "contratti" },
  { to: "/admin/bookings", icon: CalendarRange, label: t("common.bookings"), key: "prenotazioni" },
  { to: "/admin/tracking", icon: MapPin, label: "Tracking GPS", key: "tracking" },
  { to: "/admin/iot", icon: Activity, label: t("common.iot") + " 📡", key: "iot" },
  { to: "/admin/workshop", icon: Wrench, label: t("common.maintenance"), key: "officina" },
  { to: "/admin/damages", icon: AlertTriangle, label: t("common.damages"), key: "danni" },
  { to: "/admin/sinistri", icon: ShieldX, label: t("common.sinistri"), key: "sinistri" },
  { to: "/admin/customers", icon: Users, label: t("common.customers"), key: "clienti" },
  { to: "/admin/shared-blacklist", icon: ShieldAlert, label: "BL Condivisa", key: "blacklist-condivisa" },
  { to: "/admin/compliance", icon: ShieldCheck, label: "Compliance 🛡️", key: "compliance" },
  { to: "/admin/system-health", icon: Activity, label: "System Health", key: "system-health" },
  { to: "/admin/predictive", icon: Wrench, label: "Predictive 🔧", key: "predictive" },
  { to: "/admin/sos", icon: AlertCircle, label: "SOS 🚨", key: "sos" },
  { to: "/admin/marketplace", icon: Globe, label: "B2B Marketplace", key: "marketplace" },
  { to: "/admin/intelligent-suite", icon: Brain, label: "Intelligent Suite 🧠", key: "intelligent-suite" },
  { to: "/admin/eio", icon: Brain, label: "EIO Engine ⚡", key: "eio" },
  { to: "/admin/superadmin", icon: Crown, label: "Super Admin 👑", key: "superadmin" },
  { to: "/admin/whatsapp", icon: MessageCircle, label: "WhatsApp 💬", key: "whatsapp" },
  { to: "/admin/finance", icon: CreditCard, label: t("common.payments"), key: "pagamenti" },
  { to: "/admin/expenses", icon: Wallet, label: t("common.expenses"), key: "spese" },
  { to: "/admin/multe", icon: Receipt, label: t("common.multe"), key: "multe" },
  { to: "/admin/rinotifiche", icon: ShieldX, label: "Rinotifiche ✨", key: "rinotifiche" },
  { to: "/admin/pec-inbox", icon: Bell, label: "PEC Inbox AI", key: "pec-inbox" },
  { to: "/admin/sumup", icon: Wallet, label: "SumUp Pagamenti", key: "sumup" },
  { to: "/admin/storage", icon: HardDrive, label: "Cloud Storage R2", key: "storage" },
  { to: "/admin/fatture", icon: FileText, label: t("common.invoices"), key: "fatture" },
  { to: "/admin/channels", icon: Globe, label: t("common.channels") + " 🌍", key: "channels" },
  { to: "/admin/transfers", icon: Truck, label: t("common.transfers"), key: "transfers" },
  { to: "/admin/mobile-app", icon: Smartphone, label: t("common.mobile_app"), key: "mobile-app" },
  { to: "/admin/integrations", icon: Plug, label: t("common.integrations"), key: "integrations" },
  { to: "/admin/vision", icon: Eye, label: t("common.vision_hub") + " ✨", key: "vision" },
  { to: "/admin/tco", icon: TrendingUp, label: t("common.tco"), key: "tco" },
  { to: "/admin/dunning", icon: BellRing, label: t("common.dunning"), key: "dunning" },
  { to: "/admin/alerts", icon: Bell, label: "Alert", key: "alert" },
  { to: "/admin/reports", icon: BarChart3, label: "Report", key: "report" },
  { to: "/admin/sedi", icon: Building2, label: t("common.sedi"), key: "sedi" },
  { to: "/admin/plans", icon: Crown, label: t("common.plans"), key: "piani" },
  { to: "/admin/users", icon: UserCog, label: "Utenti", key: "utenti" },
  { to: "/admin/oracle", icon: Brain, label: "FabriOracle ✨", key: "oracle" },
  { to: "/admin/ai", icon: Bot, label: "AI Assistant", key: "ai" },
  { to: "/admin/commercialista", icon: Calculator, label: "Commercialista AI", key: "commercialista" },
  { to: "/admin/settings", icon: SettingsIcon, label: t("common.settings"), key: "impostazioni" },
];

// Voci visibili nella bottom tab bar mobile (priorità alta)
const MOBILE_BOTTOM = [
  { to: "/admin", icon: LayoutDashboard, label: "Home", end: true },
  { to: "/admin/fleet", icon: Car, label: "Flotta" },
  { to: "/admin/contracts", icon: FileSignature, label: "Contratti" },
  { to: "/admin/oracle", icon: Brain, label: "Oracle" },
];

const SidebarContent = ({ onItemClick, alertCount, complianceReviewCount, user, onLogout, onChangePassword }) => {
  const { t } = useTranslation();
  const items = buildItems(t);
  return (
  <>
    <div className="p-5 border-b border-white/5 flex items-center justify-center">
      <Brand size={72} />
    </div>
    <nav className="flex-1 p-3 space-y-1 overflow-y-auto custom-scroll">
      {items.map((it) => (
        <NavLink
          key={it.key}
          to={it.to}
          end={it.end}
          onClick={onItemClick}
          data-testid={`nav-${it.key}`}
          className={({ isActive }) =>
            `group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
              isActive ? "bg-white/5 text-white" : "text-white/55 hover:text-white hover:bg-white/[0.03]"
            }`
          }
        >
          {({ isActive }) => (
            <>
              {isActive && <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r bg-[#ff0033] shadow-[0_0_12px_rgba(255,0,51,0.7)]" />}
              <it.icon size={18} strokeWidth={1.6} />
              <span className="font-medium flex-1">{it.label}</span>
              {it.key === 'alert' && alertCount > 0 && (
                <span className="min-w-[18px] h-[18px] rounded-full bg-[#ff0033] text-[10px] font-bold grid place-items-center px-1">{alertCount}</span>
              )}
              {it.key === 'compliance' && complianceReviewCount > 0 && (
                <span data-testid="sidebar-compliance-badge" className="min-w-[18px] h-[18px] rounded-full bg-amber-500 text-[10px] font-bold grid place-items-center px-1 text-black animate-pulse">{complianceReviewCount}</span>
              )}
            </>
          )}
        </NavLink>
      ))}
    </nav>
    <div className="p-3 border-t border-white/5">
      <div className="px-3 py-2 text-xs text-white/40 uppercase tracking-[0.2em]">Account</div>
      <div className="px-3 py-2 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{user?.nome} {user?.cognome}</div>
          <div className="text-[11px] text-white/40 uppercase tracking-wider truncate">{user?.role}</div>
        </div>
        <button onClick={onLogout} data-testid="logout-button" className="p-2 rounded-lg hover:bg-white/5 text-white/60 hover:text-[#ff0033] shrink-0">
          <LogOut size={16} />
        </button>
      </div>
      <button
        onClick={onChangePassword}
        data-testid="change-password-link"
        className="w-full mt-1 px-3 py-2 flex items-center gap-2 text-xs text-white/70 hover:text-white hover:bg-white/5 rounded-lg transition"
      >
        <KeyRound size={13} /> Cambia password
      </button>
    </div>
  </>
  );
};

export const AdminLayout = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [alertCount, setAlertCount] = useState(0);
  const [complianceReviewCount, setComplianceReviewCount] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [lastEventTs, setLastEventTs] = useState(null);
  const [pwdDialogOpen, setPwdDialogOpen] = useState(false);

  useEffect(() => {
    const loadAlerts = () => api.get("/alerts").then((r) => {
      setAlertCount(r.data.filter((a) => a.severita === "critical" || a.severita === "high").length);
    }).catch(() => {});
    const loadCompliance = () => api.get("/compliance/review-queue").then((r) => {
      setComplianceReviewCount(r.data?.items?.length || 0);
    }).catch(() => {});

    // Toast real-time: poll ops/notifications ogni 45s
    const checkNotifications = async () => {
      try {
        const r = await api.get("/ops/notifications/pending");
        const recent = r.data?.recent_events || [];
        if (recent.length > 0 && lastEventTs && recent[0].checked_at > lastEventTs) {
          const ev = recent[0];
          const kindLabels = {
            auto_reject_blacklist: "🚫 Booking bloccato (blacklist)",
            auto_reject_blacklist_contract: "🚫 Contratto bloccato (blacklist)",
            card_funding_block: "💳 Carta rifiutata (no credit)",
            tourist_check: "🌍 Nuovo tourist check",
            review_approved: "✓ Review approvata",
            review_rejected: "✗ Review rigettata",
          };
          const label = kindLabels[ev.kind] || ev.kind;
          toast(label, { description: `Tipo: ${ev.risk_level || "compliance"} · ${ev.checked_at?.slice(11,19)}` });
        }
        if (recent.length > 0) setLastEventTs(recent[0].checked_at);
      } catch (e) { /* silent */ }
    };

    loadAlerts();
    loadCompliance();
    checkNotifications();
    const t = setInterval(() => { loadAlerts(); loadCompliance(); checkNotifications(); }, 45000);
    return () => clearInterval(t);
  }, []);

  // Chiudi drawer ad ogni cambio rotta
  useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);
  // Blocca scroll body quando drawer aperto
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const onLogout = () => { logout(); nav("/"); };
  const openPwdDialog = () => { setDrawerOpen(false); setPwdDialogOpen(true); };

  return (
    <div className="min-h-screen flex bg-[#050505]" data-testid="admin-layout">
      <ChangePasswordDialog open={pwdDialogOpen} onClose={() => setPwdDialogOpen(false)} />
      {/* ===== SIDEBAR DESKTOP ===== */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-white/5 bg-[#070708] flex-col h-screen sticky top-0">
        <SidebarContent alertCount={alertCount} complianceReviewCount={complianceReviewCount} user={user} onLogout={onLogout} onChangePassword={openPwdDialog} />
      </aside>

      {/* ===== DRAWER MOBILE ===== */}
      <AnimatePresence>
        {drawerOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setDrawerOpen(false)}
              className="lg:hidden fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
              data-testid="drawer-overlay"
            />
            <motion.aside
              initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 280 }}
              className="lg:hidden fixed top-0 bottom-0 left-0 w-[78vw] max-w-xs z-50 bg-[#070708] border-r border-white/10 flex flex-col"
              data-testid="mobile-drawer"
            >
              <SidebarContent
                onItemClick={() => setDrawerOpen(false)}
                alertCount={alertCount}
                complianceReviewCount={complianceReviewCount}
                user={user}
                onLogout={onLogout}
                onChangePassword={openPwdDialog}
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ===== MAIN ===== */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* TOPBAR */}
        <header className="sticky top-0 z-30 h-14 sm:h-16 border-b border-white/5 px-3 sm:px-6 flex items-center justify-between glass-strong"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {/* Hamburger (solo mobile) */}
            <button onClick={() => setDrawerOpen(true)} data-testid="mobile-menu-btn"
              className="lg:hidden p-2 rounded-lg hover:bg-white/5 text-white/80 -ml-1">
              <Menu size={20} />
            </button>
            {/* Brand su mobile (al posto sidebar) */}
            <div className="lg:hidden flex-1 flex justify-center">
              <Brand size={40} />
            </div>
            {/* Search desktop — trigger CommandPalette (Cmd+K) */}
            <button
              type="button"
              onClick={() => window.__openCommandPalette?.()}
              className="relative hidden lg:flex items-center bg-white/[0.03] border border-white/10 rounded-full pl-9 pr-2 py-2 text-sm w-80 hover:border-[#ff0033]/40 transition group"
              data-testid="topbar-search"
            >
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <span className="text-white/50 group-hover:text-white/70 transition flex-1 text-left">Cerca clienti, veicoli, contratti…</span>
              <kbd className="text-[10px] text-white/40 bg-white/[0.04] px-1.5 py-0.5 rounded border border-white/10">⌘K</kbd>
            </button>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <LangSwitcher compact />
            {/* Search button mobile/tablet (trigger CommandPalette) */}
            <button
              onClick={() => window.__openCommandPalette?.()}
              className="lg:hidden p-2 rounded-full hover:bg-white/5"
              data-testid="topbar-search-mobile"
              aria-label="Cerca"
            >
              <Search size={16} className="text-white/70" />
            </button>
            <button onClick={() => nav("/admin/alerts")} className="relative p-2 rounded-full hover:bg-white/5" data-testid="topbar-notifications">
              <Bell size={16} className="text-white/70" />
              {alertCount > 0 ? (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-[#ff0033] pulse-neon text-[10px] font-bold grid place-items-center px-1">
                  {alertCount}
                </span>
              ) : (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-white/20" />
              )}
            </button>
            <div className="h-8 w-8 rounded-full bg-[#ff0033]/15 border border-[#ff0033]/40 grid place-items-center text-[11px] font-bold">
              {user?.nome?.[0]}{user?.cognome?.[0]}
            </div>
          </div>
        </header>

        {/* CONTENUTO */}
        <motion.div
          key={loc.pathname}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="flex-1 p-3 sm:p-5 lg:p-6 overflow-x-hidden pb-24 lg:pb-6"
        >
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </motion.div>

        {/* ===== BOTTOM TAB BAR MOBILE ===== */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-[#070708]/95 backdrop-blur-xl border-t border-white/10 grid grid-cols-4 px-1"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          data-testid="mobile-bottom-nav"
        >
          {MOBILE_BOTTOM.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] transition relative ${
                  isActive ? 'text-[#ff0033]' : 'text-white/55 hover:text-white'
                }`
              }
              data-testid={`mobile-tab-${it.label}`}
            >
              {({ isActive }) => (
                <>
                  {isActive && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[2px] bg-[#ff0033] rounded-b" />}
                  <it.icon size={20} strokeWidth={isActive ? 2 : 1.6} />
                  <span className="font-medium">{it.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </main>

      {/* === COMMAND PALETTE (Cmd+K / Ctrl+K) — overlay globale === */}
      <CommandPalette />
    </div>
  );
};
